import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FilterChip, StatePanel } from '@3dspinit/ui';
import { api, type LibraryItem } from '../lib/api';
import { useSession } from '../context/SessionContext';

type LibraryFilter = 'created' | 'acquired' | 'favorites';

export function LibraryPage() {
  const { sessionState, authenticated, startKeyra } = useSession();
  const [params] = useSearchParams();
  const focusId = params.get('focus');
  const [owned, setOwned] = useState<LibraryItem[]>([]);
  const [acquired, setAcquired] = useState<LibraryItem[]>([]);
  const [favorites, setFavorites] = useState<LibraryItem[]>([]);
  const [filter, setFilter] = useState<LibraryFilter>('created');
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (sessionState === 'INITIALIZING') return;
    if (!authenticated) {
      setLoaded(true);
      return;
    }

    void api
      .getLibrary()
      .then((data) => {
        setOwned(data.owned);
        setAcquired(data.acquired);
        setFavorites(data.favorites ?? []);
        setError(null);
        if (focusId && data.favorites?.some((f) => f.id === focusId)) setFilter('favorites');
        else if (focusId && data.owned.some((o) => o.id === focusId)) setFilter('created');
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed'))
      .finally(() => setLoaded(true));
  }, [sessionState, authenticated, focusId]);

  const items = useMemo(() => {
    if (filter === 'acquired') return acquired;
    if (filter === 'favorites') return favorites;
    return owned;
  }, [filter, owned, acquired, favorites]);

  if (sessionState === 'INITIALIZING' || !loaded) {
    return (
      <main className="page">
        <StatePanel variant="loading" title="Loading library" message="Checking your session…" />
      </main>
    );
  }

  if (!authenticated) {
    return (
      <main className="page">
        <StatePanel
          variant="default"
          title="Sign in to open My Library"
          message="Favorites and drafts require Keyra-verified identity. Favorites are references — not licenses."
          actionLabel="Sign in with Keyra"
          onAction={() => void startKeyra('/library')}
        />
      </main>
    );
  }

  return (
    <main className="page">
      <h1 style={{ fontFamily: 'var(--font-display)' }}>My Library</h1>
      <p className="muted">
        Created ({owned.length}) · Acquired ({acquired.length}) · Favorites ({favorites.length}). Favorites ≠ ownership.
      </p>
      {error && <p className="err" role="alert">{error}</p>}

      <div className="category-row" role="tablist" aria-label="Library filters">
        <FilterChip selected={filter === 'created'} onClick={() => setFilter('created')}>
          Created
        </FilterChip>
        <FilterChip selected={filter === 'acquired'} onClick={() => setFilter('acquired')}>
          Acquired
        </FilterChip>
        <FilterChip selected={filter === 'favorites'} onClick={() => setFilter('favorites')}>
          Favorites
        </FilterChip>
      </div>

      <div className="masonry" style={{ marginTop: '1rem' }}>
        {items.map((item) => (
          <article
            key={item.id}
            className={`object-card ${focusId === item.id ? 'object-card--focus' : ''}`}
            id={focusId === item.id ? 'library-focus' : undefined}
          >
            <Link to={`/spin/${item.id}`}>
              <img className="poster r11" src={item.posterUrl ?? undefined} alt="" />
            </Link>
            <div className="meta">
              <h2>{item.title}</h2>
              <p className="muted">
                {'origin' in item && item.origin ? `${item.origin} · ` : ''}
                {item.status}
                {focusId === item.id ? ' · just saved' : ''}
              </p>
              <div className="row">
                <Link className="btn" to={`/spin/${item.id}`}>
                  Inspect
                </Link>
                <Link className="btn" to={`/workspace?asset=${item.id}`}>
                  Workspace
                </Link>
                {filter === 'created' && item.status === 'private' && (
                  <Link className="btn primary" to={`/publish/${item.id}`}>
                    Publish
                  </Link>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
      {!items.length && (
        <p className="muted">
          Nothing here yet.{' '}
          <Link to="/workspace">Create or import an object</Link>.
        </p>
      )}

      <div className="row" style={{ marginTop: '1rem' }}>
        <Link className="btn primary" to="/workspace">
          Create workspace
        </Link>
        <Link className="btn" to="/upload">
          Upload
        </Link>
      </div>
    </main>
  );
}
